import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["children", "ContentWrapper", "buttonGroup", "onOpenChange", "open"];
import { Children, cloneElement, useEffect, useMemo, useRef, useState } from 'react';
import { syntheticEvent } from '../../utils/SyntheticEvent';
import styled from 'foundations-theming/utils';
import { ButtonGroup } from '../ButtonGroup';
import { Controllable } from '../Controllable';
import { FloatingButtonGroupContextProvider } from './FloatingButtonGroupContext';
import devLogger from 'react-utils/devLogger';
import { filterProps } from '../../../utils/filterProps';
import { useMapTokenPropsToCSS } from '../../../hooks/useMapTokenPropsToCSS';
import { warnIfFragment } from '../../utils/warnIfFragment';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const DefaultContentWrapper = ({
  children,
  open
}) => {
  const onlyChild = children && Children.only(children);
  warnIfFragment(onlyChild, 'FloatingButtonGroup');
  return /*#__PURE__*/_jsx("div", {
    children: onlyChild && /*#__PURE__*/cloneElement(onlyChild, {
      open
    })
  });
};
const maybeRenderButtonsWithClasses = buttonGroup => {
  if (process.env.NODE_ENV !== 'production') {
    if (buttonGroup.type !== ButtonGroup) {
      devLogger.warn({
        message: 'FloatingButtonGroup: Component in buttonGroup must be a ButtonGroup component.',
        key: 'FloatingButtonGroup: buttonGroup'
      });
    }
  }
  return /*#__PURE__*/cloneElement(buttonGroup);
};
const StyledFloatingButtonGroup = styled.div.withConfig({
  displayName: "FloatingButtonGroup__StyledFloatingButtonGroup",
  componentId: "jkidy8-0"
})(({
  $cssStyles
}) => {
  return Object.assign({
    position: 'relative'
  }, $cssStyles);
});
export const FloatingButtonGroupComponent = _ref => {
  let {
      children,
      ContentWrapper = DefaultContentWrapper,
      buttonGroup,
      onOpenChange,
      open = false
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  const [hasFocus, setHasFocus] = useState(false);
  const [hasMouseover, setHasMouseover] = useState(false);
  const [hasPopover, setHasPopover] = useState(false);
  const [activeDropdownParent, setActiveDropdownParent] = useState(null);
  const wrapperRef = useRef(null);
  const onOpenChangeRef = useRef(onOpenChange);
  onOpenChangeRef.current = onOpenChange;
  useEffect(() => {
    const observer = new MutationObserver(() => {
      var _wrapperRef$current;
      setHasPopover(!!((_wrapperRef$current = wrapperRef.current) !== null && _wrapperRef$current !== void 0 && _wrapperRef$current.querySelector('[data-popover-id]')));
    });
    if (wrapperRef.current) {
      observer.observe(wrapperRef.current, {
        attributes: true,
        subtree: true
      });
    }
    return () => {
      observer.disconnect();
    };
  }, []);
  useEffect(() => {
    var _onOpenChangeRef$curr;
    const shouldBeOpen = hasFocus || hasMouseover || hasPopover;

    // This ref prevents infinite update loops when `open` is controlled.
    (_onOpenChangeRef$curr = onOpenChangeRef.current) === null || _onOpenChangeRef$curr === void 0 || _onOpenChangeRef$curr.call(onOpenChangeRef, syntheticEvent(shouldBeOpen));
    if (activeDropdownParent && hasPopover) {
      activeDropdownParent.focus();
    }
  }, [hasFocus, hasMouseover, hasPopover, activeDropdownParent]);
  const handleBlur = () => setHasFocus(false);
  const handleFocus = () => setHasFocus(true);
  const handleMouseEnter = () => setHasMouseover(true);
  const handleMouseMove = () => {
    if (!hasMouseover) {
      setHasMouseover(true);
    }
  };
  const handleMouseLeave = () => setHasMouseover(false);
  const {
    domProps,
    tokenProps
  } = filterProps(rest);
  const cssStyles = useMapTokenPropsToCSS(tokenProps);
  const contextValue = useMemo(() => ({
    setFloatingButtonGroupActiveEl: setActiveDropdownParent,
    floatingGroup: true,
    srOnly: !open
  }), [setActiveDropdownParent, open]);
  return /*#__PURE__*/_jsxs(StyledFloatingButtonGroup, Object.assign({}, domProps, {
    ref: wrapperRef,
    onMouseEnter: handleMouseEnter,
    onMouseLeave: handleMouseLeave,
    onMouseMove: handleMouseMove,
    onFocus: handleFocus,
    onBlur: handleBlur,
    $cssStyles: cssStyles,
    children: [/*#__PURE__*/_jsx(FloatingButtonGroupContextProvider, {
      value: contextValue,
      children: maybeRenderButtonsWithClasses(buttonGroup)
    }), /*#__PURE__*/_jsx(ContentWrapper, {
      open: open,
      children: children
    })]
  }));
};

// Consider removing & updating if we ever adapt the Controllable component API to accept defaults
// eslint-disable-next-line hubspot-dev/no-default-props-assignment
FloatingButtonGroupComponent.defaultProps = {
  open: false
};
export const FloatingButtonGroup = Controllable(FloatingButtonGroupComponent, ['open']);